import MainFooter from "../../Components/Footer/MainFooter";
import MainHeader from "../../Components/Header/MainHeader";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useState, useMemo } from "react";
import { db } from "../../Database/RekebishaAdmin/config";
import UserCard from "../../Components/UserCard";
import countiesData from "../../data/county.json";
import {
  Shield,
  Star,
  Handshake,
  SlidersHorizontal,
  Search,
  Play,
  Phone,
  Wrench,
  ArrowRight,
  ArrowDown,
  Smartphone,
} from "lucide-react";

const CLIENT_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.rekebisha.RekebishaClient&pcampaignid=web_share";
const FUNDI_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.whim.RekebishaFundi001&pcampaignid=web_share";
const DEMO_URL = "https://youtube.com/shorts/EInCdy6M0jc";

const heroImage = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1920&auto=format&fit=crop";

// ✅ Hardcoded allowed professions (only these will appear in dropdown)
const ALLOWED_PROFESSIONS = [
  'Painter',
  'Household Electronics Expert',
  'Fumigation Expert',
  'Electrician and Electrical fittings',
  'Plumber and Sanitary Fittings',
  'Carpenter'
].map(name => ({ name }));

const FindFundi = () => {
  const [selectedProfession, setSelectedProfession] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedSubCounty, setSelectedSubCounty] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(9);
  const [emptyData, setEmptyData] = useState(false);
  const [professions] = useState(ALLOWED_PROFESSIONS); // ✅ static list
  const [counties, setCounties] = useState([]);
  const [subCounties, setSubCounties] = useState([]);
  const [totalCount, setTotalCount] = useState(null);

  const getAllSubCounties = useMemo(() => {
    return () => {
      if (!selectedCounty?.name) return [];
      const county = countiesData.County.find(
        (c) => c.county_name === selectedCounty.name
      );
      if (county) {
        return county.constituencies.map((constituency) => ({
          name: constituency.constituency_name,
        }));
      }
      return [];
    };
  }, [selectedCounty]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let collectionRef = db.collection("FundiAppUsers");
        const countQuerySnapshot = await collectionRef.get();
        const totalCount = countQuerySnapshot.size;
        setTotalCount(totalCount);

        // Apply profession filter only if valid
        if (selectedProfession) {
          // Optional: extra safety check
          if (!ALLOWED_PROFESSIONS.some(p => p.name === selectedProfession.name)) {
            console.warn("Disallowed profession selected:", selectedProfession);
            return;
          }
          collectionRef = collectionRef.where("profession", "==", selectedProfession.name);
        }

        if (selectedCounty) {
          collectionRef = collectionRef.where("countylocation", "==", selectedCounty.name);
        }

        if (selectedSubCounty) {
          collectionRef = collectionRef.where("subcounty", "==", selectedSubCounty.name);
        }

        const querySnapshot = await collectionRef
          .orderBy("competenceRating", "desc")
          .limit(limit)
          .get();

        const fetchedData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setData(fetchedData);
        setLoading(false);
        setEmptyData(fetchedData.length === 0);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
        setData([]);
        setEmptyData(true);
      }
    };

    const getFilters = async () => {
      try {
        // Only fetch counties (professions are static)
        let collectionRef = db.collection("FundiAppUsers");
        const querySnapshot = await collectionRef.get();
        const uniqueCounties = {};

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.countylocation) {
            uniqueCounties[data.countylocation] =
              (uniqueCounties[data.countylocation] || 0) + 1;
          }
        });

        const filteredCounties = Object.keys(uniqueCounties).map((name) => ({
          name,
          count: uniqueCounties[name],
        }));

        setCounties(filteredCounties);
        // professions already set statically
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };

    fetchData();
    getFilters();
  }, [limit, selectedProfession, selectedCounty, selectedSubCounty, getAllSubCounties]);

  useEffect(() => {
    if (selectedCounty) {
      setSubCounties(getAllSubCounties());
      setSelectedSubCounty(null);
    } else {
      setSubCounties([]);
    }
  }, [selectedCounty, getAllSubCounties]);

  const handleCountyChange = (e) => {
    setSelectedCounty(e.value);
  };

  // Simplified option template (no count for professions)
  const optionTemplate = (option) => {
    return (
      <div className="flex items-center justify-between gap-3 w-full py-1">
        <div className="text-navy font-medium">{option.name}</div>
        {option.count !== undefined && (
          <div className="bg-brandgreen font-bold text-xs px-3 py-1 text-white rounded-full">
            {option.count}
          </div>
        )}
      </div>
    );
  };

  const paginate = () => {
    setLimit((prevLimit) => prevLimit + 9);
  };

  const dropdownClass =
    "w-full border border-gray-200 rounded-lg shadow-sm hover:border-brandgreen focus:border-brandgreen transition-colors";

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <MainHeader />

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-navy/75"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 relative">
          <div className="max-w-2xl">
            <span className="inline-block text-brandgreen font-semibold tracking-wide uppercase text-sm mb-4">
              Kenya's Marketplace For Homes And Fundis
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Find Your Perfect Skilled Fundi
            </h1>
            <p className="text-lg text-gray-200 mb-10 max-w-xl">
              Connect with verified, highly rated professionals across Kenya. From electricians to
              plumbers, find the right expert for your job and talk to them directly.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <a
                href="#fundi-results"
                className="inline-flex items-center justify-center px-8 py-4 bg-brandgreen text-white font-bold rounded-lg hover:bg-brandgreen-dark transition-colors duration-300"
              >
                <Search className="w-5 h-5 mr-2" />
                Browse Fundis
              </a>
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-navy transition-colors duration-300"
              >
                <Play className="w-5 h-5 mr-2" />
                Watch The Demo
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-200">
              <div className="flex items-center">
                <Shield className="w-5 h-5 text-brandgreen mr-2" />
                Verified Professionals
              </div>
              <div className="flex items-center">
                <Star className="w-5 h-5 text-brandgreen mr-2" />
                Rated By Real Clients
              </div>
              <div className="flex items-center">
                <Handshake className="w-5 h-5 text-brandgreen mr-2" />
                Direct Contact, No Middlemen
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section id="fundi-results" className="py-16 bg-gray-50 flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <div className="w-full lg:w-80 shrink-0">
              <div className="lg:sticky lg:top-8 space-y-6">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-navy px-6 py-4">
                    <h3 className="text-white font-bold text-lg flex items-center gap-2">
                      <SlidersHorizontal className="w-5 h-5 text-brandgreen" />
                      Filter And Search
                    </h3>
                  </div>

                  <div className="p-6 space-y-6">
                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-navy uppercase tracking-wide">
                        Profession
                      </label>
                      <Dropdown
                        value={selectedProfession}
                        onChange={(e) => setSelectedProfession(e.target.value)}
                        itemTemplate={optionTemplate}
                        options={professions}
                        optionLabel="name"
                        placeholder="Select Profession"
                        showClear
                        className={dropdownClass}
                      />
                    </div>

                    <div className="border-t border-gray-100 pt-6">
                      {counties && (
                        <div className="space-y-3">
                          <label className="text-sm font-semibold text-navy uppercase tracking-wide">
                            County
                          </label>
                          <Dropdown
                            value={selectedCounty}
                            onChange={handleCountyChange}
                            itemTemplate={optionTemplate}
                            options={counties}
                            optionLabel="name"
                            placeholder="Select County"
                            showClear
                            className={`${dropdownClass} capitalize`}
                          />
                        </div>
                      )}
                    </div>

                    <div className="border-t border-gray-100 pt-6">
                      {subCounties && (
                        <div className="space-y-3">
                          <label className="text-sm font-semibold text-navy uppercase tracking-wide">
                            Sub County
                          </label>
                          <Dropdown
                            value={selectedSubCounty}
                            onChange={(e) => setSelectedSubCounty(e.target.value)}
                            options={subCounties}
                            optionLabel="name"
                            placeholder="Select Sub County"
                            showClear
                            className={`${dropdownClass} capitalize`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Client App CTA */}
                <div className="bg-navy rounded-2xl overflow-hidden">
                  <div className="p-8 text-center">
                    <div className="w-14 h-14 bg-brandgreen rounded-full flex items-center justify-center mx-auto mb-5">
                      <Smartphone className="w-7 h-7 text-white" />
                    </div>
                    <h4 className="text-white font-bold text-xl mb-2">Need A Service?</h4>
                    <p className="text-gray-300 text-sm mb-6">
                      Request a fundi and track the job from the Rekebisha app.
                    </p>
                    <a
                      href={CLIENT_PLAY_STORE_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full inline-flex items-center justify-center px-6 py-3 bg-brandgreen text-white font-bold rounded-lg hover:bg-brandgreen-dark transition-colors duration-300"
                    >
                      Download The App
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </a>
                  </div>
                </div>

                {/* Join as a Fundi CTA */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-8 text-center">
                  <div className="w-14 h-14 bg-brandgreen/10 rounded-full flex items-center justify-center mx-auto mb-5">
                    <Wrench className="w-7 h-7 text-brandgreen" />
                  </div>
                  <h4 className="text-navy font-bold text-xl mb-2">Are You A Fundi?</h4>
                  <p className="text-gray-600 text-sm mb-6">
                    Build a portfolio, collect ratings and get hired in your county.
                  </p>
                  <a
                    href={FUNDI_PLAY_STORE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full inline-flex items-center justify-center px-6 py-3 bg-navy text-white font-bold rounded-lg hover:bg-navy-dark transition-colors duration-300 mb-4"
                  >
                    Join As A Fundi
                  </a>
                  <a
                    href="tel:0733212124"
                    className="inline-flex items-center justify-center text-navy font-bold hover:text-brandgreen transition-colors duration-300"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    0733 212 124
                  </a>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Stats Bar */}
              <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  {totalCount ? (
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 bg-brandgreen rounded-full animate-pulse"></span>
                      <span className="text-lg font-bold text-navy">
                        {totalCount} Verified Fundis
                      </span>
                    </div>
                  ) : (
                    <div className="h-6 bg-gray-200 rounded-full w-48 animate-pulse"></div>
                  )}

                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-brandgreen" />
                      <span>Top Rated First</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-brandgreen" />
                      <span>Verified</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Results Section */}
              {!emptyData ? (
                <div className="space-y-8">
                  {data.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {data.map((item) => (
                          <UserCard
                            key={item.id}
                            id={item.id}
                            firstname={item.firstname}
                            lastname={item.lastname}
                            profession={item.profession}
                            profileImageUrl={item.profileimage}
                            rating={item.competenceRating}
                          />
                        ))}
                      </div>

                      <div className="flex justify-center pt-4">
                        {loading ? (
                          <button
                            disabled
                            className="inline-flex items-center px-8 py-4 bg-gray-100 text-gray-400 rounded-lg font-bold cursor-not-allowed"
                          >
                            <svg
                              className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-400"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Loading More Fundis
                          </button>
                        ) : (
                          <button
                            onClick={paginate}
                            className="inline-flex items-center px-8 py-4 bg-brandgreen text-white rounded-lg font-bold hover:bg-brandgreen-dark transition-colors duration-300"
                          >
                            Load More Fundis
                            <ArrowDown className="w-5 h-5 ml-2" />
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-center items-center py-20">
                      <div className="animate-pulse text-gray-600 text-lg">
                        Finding the best fundis for you...
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                  <div className="w-24 h-24 bg-brandgreen/10 rounded-full flex items-center justify-center mx-auto mb-8">
                    <Search className="w-12 h-12 text-brandgreen" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4">No Fundis Found</h3>
                  <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
                    We could not find any fundis matching your criteria. We are constantly adding new
                    professionals to the platform.
                  </p>
                  <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
                    <span>Try different filters</span>
                    <span>Expand your search area</span>
                    <span>Check back soon</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Looking For A Home Too?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Rekebisha is also where Kenyans find houses to let or buy, directly from the homeowner or
            agent. One app for both sides of your move.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a
              href="/find-rental"
              className="inline-flex items-center px-8 py-4 bg-brandgreen text-white font-bold rounded-lg hover:bg-brandgreen-dark transition-colors duration-300"
            >
              <Search className="w-5 h-5 mr-2" />
              Browse Properties
            </a>
            <a
              href={CLIENT_PLAY_STORE_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-navy transition-colors duration-300"
            >
              <Play className="w-5 h-5 mr-2" />
              Download The App
            </a>
          </div>
        </div>
      </section>

      <MainFooter />
    </div>
  );
};

export default FindFundi;
